/**
 * Map icon names to Unicode symbols/emojis for native components.
 * Keys include both legacy MUI names (for backward compatibility) and Lucide PascalCase names.
 */
export const iconMap: Record<string, string> = {
  // Navigation & Actions
  Home: '🏠',
  Settings: '⚙️',
  Search: '🔍',
  Add: '➕',
  Plus: '➕',
  Edit: '✏️',
  Pencil: '✏️',
  Delete: '🗑️',
  Trash2: '🗑️',
  Save: '💾',
  Close: '✖️',
  X: '✖️',
  Check: '✓',
  Menu: '☰',
  MoreVert: '⋮',
  MoreHoriz: '⋯',
  Ellipsis: '⋯',
  EllipsisVertical: '⋮',

  // Arrows
  ArrowBack: '←',
  ArrowForward: '→',
  ArrowUpward: '↑',
  ArrowDownward: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ChevronLeft: '‹',
  ChevronRight: '›',
  ExpandMore: '▼',
  ChevronDown: '▼',
  ExpandLess: '▲',
  ChevronUp: '▲',
  North: '⬆',
  South: '⬇',
  East: '➡',
  West: '⬅',

  // Status & Info
  Info: 'ℹ️',
  Warning: '⚠️',
  TriangleAlert: '⚠️',
  Error: '❌',
  CircleX: '❌',
  Help: '❓',
  CircleHelp: '❓',
  CheckCircle: '✅',
  CircleCheck: '✅',
  Cancel: '🚫',
  Ban: '🚫',
  Block: '⛔',
  Flag: '🚩',
  PriorityHigh: '❗',

  // People
  Person: '👤',
  User: '👤',
  Group: '👥',
  Users: '👥',
  PersonAdd: '➕👤',
  UserPlus: '➕👤',
  PersonRemove: '➖👤',
  UserMinus: '➖👤',
  SupervisorAccount: '👔',
  ManageAccounts: '👤⚙️',
  CircleUser: '👤',

  // Communication
  Email: '📧',
  Mail: '📧',
  Phone: '📞',
  Chat: '💬',
  MessageSquare: '💬',
  Message: '✉️',
  Notifications: '🔔',
  Bell: '🔔',
  NotificationsOff: '🔕',
  BellOff: '🔕',
  Send: '📤',
  SendHorizonal: '📤',
  Campaign: '📢',
  Megaphone: '📢',
  Announcement: '📣',

  // Files & Folders
  Folder: '📁',
  FolderOpen: '📂',
  InsertDriveFile: '📄',
  File: '📄',
  Description: '📝',
  FileText: '📝',
  AttachFile: '📎',
  Paperclip: '📎',
  Link: '🔗',

  // Media
  Image: '🖼️',
  PhotoCamera: '📷',
  Camera: '📷',
  Videocam: '📹',
  Video: '📹',
  Mic: '🎤',
  Microphone: '🎤',
  VolumeUp: '🔊',
  Volume2: '🔊',
  VolumeOff: '🔇',
  VolumeX: '🔇',
  PlayArrow: '▶️',
  Play: '▶️',
  Pause: '⏸️',
  Stop: '⏹️',
  SkipPrevious: '⏮️',
  SkipBack: '⏮️',
  SkipNext: '⏭️',
  SkipForward: '⏭️',

  // Favorites & Ratings
  Star: '⭐',
  StarBorder: '☆',
  Favorite: '❤️',
  Heart: '❤️',
  FavoriteBorder: '♡',
  Bookmark: '🔖',
  BookmarkBorder: '🏷️',

  // Actions
  Share: '📤',
  Share2: '📤',
  Print: '🖨️',
  Printer: '🖨️',
  Download: '⬇️',
  Upload: '⬆️',
  ContentCopy: '📋',
  Copy: '📋',
  ContentPaste: '📌',
  ClipboardPaste: '📌',
  ContentCut: '✂️',
  Scissors: '✂️',
  Undo: '↩️',
  Undo2: '↩️',
  Redo: '↪️',
  Redo2: '↪️',
  Refresh: '🔄',
  RefreshCw: '🔄',
  Sync: '🔃',

  // Cloud
  Cloud: '☁️',
  CloudUpload: '☁️⬆',
  CloudDownload: '☁️⬇',
  CloudOff: '☁️',

  // Views
  FilterList: '🔽',
  Filter: '🔽',
  Sort: '↕️',
  ArrowUpDown: '↕️',
  ViewList: '📋',
  List: '📋',
  ViewModule: '⊞',
  LayoutGrid: '⊞',
  GridView: '▦',
  Grid: '▦',
  TableChart: '📊',
  Table: '📊',
  Dashboard: '📊',
  LayoutDashboard: '📊',
  BarChart: '📊',
  BarChart3: '📊',
  PieChart: '🥧',
  Timeline: '📈',
  TrendingUp: '📈',

  // Time & Calendar
  CalendarToday: '📅',
  Calendar: '📅',
  CalendarDays: '📅',
  CalendarMonth: '📅',
  Event: '📆',
  Schedule: '🗓️',
  AccessTime: '🕐',
  Clock: '🕐',
  Alarm: '⏰',
  AlarmClock: '⏰',
  Timer: '⏱️',

  // Location
  LocationOn: '📍',
  MapPin: '📍',
  Map: '🗺️',
  Public: '🌐',
  Globe: '🌐',
  Language: '🌍',
  Languages: '🌍',
  Translate: '🌐',
  Explore: '🧭',
  Compass: '🧭',
  TravelExplore: '✈️🔍',
  CompassCalibration: '🧭',

  // Shopping & Commerce
  ShoppingCart: '🛒',
  ShoppingBag: '🛍️',
  Storefront: '🏪',
  Store: '🏪',
  LocalOffer: '🏷️',
  Payment: '💳',
  CreditCard: '💳',
  AccountBalance: '🏦',
  Landmark: '🏦',
  AccountBalanceWallet: '👛',
  Wallet: '👛',
  Receipt: '🧾',
  Inventory: '📦',
  Package: '📦',
  LocalShipping: '🚚',
  Truck: '🚚',

  // Security
  Security: '🛡️',
  Shield: '🛡️',
  Lock: '🔒',
  LockOpen: '🔓',
  Unlock: '🔓',
  Key: '🔑',
  VpnKey: '🗝️',
  KeyRound: '🗝️',
  Visibility: '👁️',
  Eye: '👁️',
  VisibilityOff: '🙈',
  EyeOff: '🙈',
  VerifiedUser: '✅🛡️',
  ShieldCheck: '✅🛡️',
  AdminPanelSettings: '🛡️⚙️',

  // Development
  Code: '💻',
  Terminal: '⌨️',
  TerminalSquare: '⌨️',
  DataObject: '{ }',
  Braces: '{ }',
  Storage: '💾',
  HardDrive: '💾',
  Dns: '🖧',
  Api: '🔌',
  Plug: '🔌',
  IntegrationInstructions: '🔧',
  BugReport: '🐛',
  Bug: '🐛',
  Speed: '⚡',
  Zap: '⚡',

  // Business
  Business: '🏢',
  Building2: '🏢',
  Work: '💼',
  Briefcase: '💼',
  School: '🎓',
  GraduationCap: '🎓',
  Science: '🔬',
  Microscope: '🔬',
  Biotech: '🧬',
  Dna: '🧬',
  HealthAndSafety: '🏥',
  LocalHospital: '🏥',
  Hospital: '🏥',
  MedicalServices: '⚕️',

  // Tools
  Build: '🔧',
  Wrench: '🔧',
  Construction: '🚧',
  Handyman: '🔨',
  Hammer: '🔨',
  Extension: '🧩',
  Puzzle: '🧩',
  Widgets: '📦',
  Category: '📂',
  Label: '🏷️',
  Tag: '🔖',
  Shirt: '👕',
  Tune: '🎛️',
  SlidersHorizontal: '🎛️',
  DisplaySettings: '🖥️',
  Monitor: '🖥️',
  SettingsApplications: '⚙️',

  // Devices
  Apps: '📱',
  AccountTree: '🌳',
  GitBranch: '🌳',
  Hub: '🔗',
  Devices: '📱',
  Computer: '💻',
  PhoneAndroid: '📱',
  Tablet: '📱',
  Watch: '⌚',
  Tv: '📺',
  Cast: '📡',
  Bluetooth: '🔵',
  Wifi: '📶',
  SignalCellularAlt: '📶',
  BatteryFull: '🔋',
  Power: '🔌',

  // Theme
  LightMode: '☀️',
  Sun: '☀️',
  DarkMode: '🌙',
  Moon: '🌙',
  Contrast: '◐',
  Palette: '🎨',
  Paintbrush: '🖌️',
  Brush: '🖌️',
  FormatPaint: '🎨',

  // Text Formatting
  TextFields: '🔤',
  FormatBold: '𝐁',
  FormatItalic: '𝐼',
  FormatUnderlined: 'U̲',
  FormatListBulleted: '•',
  FormatListNumbered: '1.',
  FormatAlignLeft: '⬅',
  FormatAlignCenter: '⬌',
  FormatAlignRight: '➡',
  FormatQuote: '❝',

  // Navigation
  Login: '🚪➡',
  Logout: '🚪⬅',
  OpenInNew: '↗️',
  OpenInBrowser: '🌐',
  Launch: '🚀',
  ExitToApp: '🚪',
  Flight: '✈️',
  DirectionsCar: '🚗',
  DirectionsBus: '🚌',
  Train: '🚆',

  // Tasks
  Assignment: '📋',
  AssignmentTurnedIn: '✅📋',
  Task: '☑️',
  TaskAlt: '✅',
  DoNotDisturb: '🚫',
  Report: '📑',
  ReportProblem: '⚠️',

  // Ideas
  TipsAndUpdates: '💡',
  Lightbulb: '💡',
  EmojiObjects: '💡',
  Psychology: '🧠',
  AutoAwesome: '✨',
  AutoFixHigh: '🪄',
  SmartButton: '🔘',
  SmartToy: '🤖',
  Bot: '🤖',
  RocketLaunch: '🚀',
  Rocket: '🚀',
  Sparkles: '✨',
  Brain: '🧠',
  Wand2: '🪄',

  // Misc
  NewReleases: '🆕',
  Crop: '✂️',
  ZoomIn: '🔍➕',
  ZoomOut: '🔍➖',
  Fullscreen: '⛶',
  Maximize: '⛶',
  FullscreenExit: '⛶',
  Minimize: '⛶',
  GroupAdd: '👥➕',
  GroupRemove: '👥➖',
  InsertLink: '🔗',
  InsertPhoto: '🖼️',
  InsertChart: '📊',
  TableRows: '▤',
  ViewColumn: '▥',
};

export const iconNames = Object.keys(iconMap);
